import React, { useEffect, useRef, useState } from 'react'
import { Button, Stack, Box } from '@mui/material'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'

interface AvatarProps {
  videoUrl: string
}

const Avatar: React.FC<AvatarProps> = ({ videoUrl }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  let canvas, ctx, video

  const handlePlayClick = () => {
    video.play()
  }

  const handlePauseClick = () => {
    video.pause()
  }

  useEffect(() => {
    canvas = canvasRef.current
    ctx = canvas.getContext('2d')
    video = document.createElement('video')

    video.src = videoUrl + '#t=0.3'
    video.crossOrigin = 'anonymous'
    video.playsInline = true
    video.muted = false
    video.loop = false
    video.autoplay = false

    video.addEventListener('loadeddata', () => {
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight / 2

      const renderFrame = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(
          video,
          0,
          0,
          canvas.width,
          canvas.height,
          0,
          0,
          canvas.width,
          canvas.height
        )

        const alphaChannelData = getAlphaChannel(video, ctx, canvas)
        applyAlphaChannel(ctx, alphaChannelData, canvas)

        requestAnimationFrame(renderFrame)
      }

      renderFrame()
    })

    return () => {
      video.pause()
    }
  }, [videoUrl, video])

  const getAlphaChannel = (
    video: HTMLVideoElement,
    ctx: CanvasRenderingContext2D,
    canvas: HTMLCanvasElement
  ) => {
    const alphaCanvas = document.createElement('canvas')
    alphaCanvas.width = canvas.width
    alphaCanvas.height = canvas.height
    const alphaCtx = alphaCanvas.getContext('2d')

    alphaCtx.drawImage(
      video,
      0,
      canvas.height,
      canvas.width,
      canvas.height,
      0,
      0,
      canvas.width,
      canvas.height
    )
    return alphaCtx.getImageData(0, 0, alphaCanvas.width, alphaCanvas.height)
  }

  const applyAlphaChannel = (
    ctx: CanvasRenderingContext2D,
    alphaData: ImageData,
    canvas: HTMLCanvasElement
  ) => {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
    const data = imageData.data
    const alphaDataPixels = alphaData.data

    for (let i = 0; i < data.length; i += 4) {
      data[i + 3] = alphaDataPixels[i] * (alphaDataPixels[i + 3] / 255)
    }

    ctx.putImageData(imageData, 0, 0)
  }

  return (
    <Stack alignItems={'center'}>
      <Box component={'div'} aria-label='Tour Guide Video'>
        <canvas ref={canvasRef} />
      </Box>
      <Box>
        <Button
          aria-label='Play Video'
          sx={{ width: '110px', color: 'black', backgroundColor: '#f2f3f4' }}
          variant='contained'
          onClick={handlePlayClick}
        >
          <PlayCircleOutlineIcon />
        </Button>
        <Button
          aria-label='Pause Video'
          sx={{
            width: '110px',
            color: 'black',
            backgroundColor: '#f2f3f4',
            marginLeft: '8px',
          }}
          variant='contained'
          onClick={handlePauseClick}
        >
          <PauseCircleOutlineIcon />
        </Button>
      </Box>
    </Stack>
  )
}

export default Avatar
